import type { ParamsType } from 'helpers/createUrlWithParams/createUrlWithParams';

import {
  ActionLibraryInstance,
  ActionLibraryInstanceDTO,
  ActionLibraryInstanceExportParams,
  ActionLibraryInstanceStep,
  ActionLibraryInstanceStepDTO,
  ActionLibraryInstanceStepStatus,
  ASSET_ACTION_INSTANCE_EXPORT_DEFAULT_FILENAMES,
  AssetActionInstanceExportFormat,
  BulkCreateActionLibraryInstancePayload,
  BulkCreateActionLibraryInstanceDTO,
  BulkCreateActionLibraryInstanceAdhocData,
  BulkCreateActionLibraryInstanceAdhocDataDTO,
  BulkCreateActionLibraryInstanceAdhocStep,
  BulkCreateActionLibraryInstanceAdhocStepDTO,
  CompleteActionLibraryInstancePayload,
  DelegateActionLibraryInstancePayload,
  ReopenInstancePayload,
  ReturnToOwnerActionLibraryInstancePayload,
  InstanceActivityLogEntry,
  InstanceActivityLogEntryDTO,
  InstanceAreaData,
  InstanceAreaDataDTO,
  InstanceComment,
  InstanceCommentCreatePayload,
  InstanceCommentDTO,
  InstanceDashboardMetrics,
  InstanceDashboardMetricsCreatedPeriod,
  InstanceDashboardMetricsDTO,
  InstanceSubAreaData,
  InstanceSubAreaDataDTO,
  TemplateStepOverride,
  TemplateStepOverrideDTO,
  UpdateActionLibraryInstancePayload,
} from './actionLibraryInstance.types';

const parseCreatedPeriodForFE = (dto: Record<string, number>): InstanceDashboardMetricsCreatedPeriod => ({
  total: dto.total ?? 0,
  completed: dto.completed ?? 0,
  not_started: dto.not_started ?? 0,
});

export const parseInstanceDashboardMetricsForFE = (dto: InstanceDashboardMetricsDTO): InstanceDashboardMetrics => ({
  percentageCompletedInstances: dto.percentage_completed_instances,
  activeInstancesCount: dto.active_instances_count,
  overdueActiveInstancesCount: dto.overdue_active_instances_count,
  createdLast30Days: parseCreatedPeriodForFE(dto.created_last_30_days ?? {}),
  createdLast90Days: parseCreatedPeriodForFE(dto.created_last_90_days ?? {}),
  createdLast180Days: parseCreatedPeriodForFE(dto.created_last_180_days ?? {}),
  createdLast270Days: parseCreatedPeriodForFE(dto.created_last_270_days ?? {}),
  createdLast365Days: parseCreatedPeriodForFE(dto.created_last_365_days ?? {}),
});

const parseInstanceAreaDataForFE = (dto: InstanceAreaDataDTO): InstanceAreaData => ({
  id: dto.id,
  name: dto.name,
  nameTranslationKey: dto.name_translation_key,
  code: dto.code,
  displayName: dto.display_name,
  displayNameTranslationKey: dto.display_name_translation_key,
  slug: dto.slug,
  isActive: dto.is_active,
});

const parseInstanceSubAreaDataForFE = (dto: InstanceSubAreaDataDTO): InstanceSubAreaData => ({
  id: dto.id,
  name: dto.name,
  nameTranslationKey: dto.name_translation_key,
  slug: dto.slug,
  isActive: dto.is_active,
  ...(dto.area && { area: parseInstanceAreaDataForFE(dto.area) }),
});

const parseOptionalBooleanFromDTO = (value: string | boolean | undefined | null): boolean | undefined => {
  if (value == null) return undefined;
  if (typeof value === 'boolean') return value;
  return value === 'true' || value === '1';
};

const parseTemplateTagsFromDTO = (value: string | Record<string, unknown> | undefined | null): Record<string, unknown> | undefined => {
  if (value == null) return undefined;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return undefined;
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : undefined;
  } catch {
    return undefined;
  }
};

export const parseActionLibraryInstanceStepForFE = (dto: ActionLibraryInstanceStepDTO): ActionLibraryInstanceStep => ({
  id: dto.id,
  title: dto.title,
  instruction: dto.instruction,
  mandatory: dto.mandatory,
  order: dto.order,
  isCompleted: dto.is_completed ?? dto.status === 'completed',
  completedAt: dto.completed_at ?? null,
  completedBy: dto.completed_by ?? null,
  status: dto.status as ActionLibraryInstanceStepStatus | undefined,
  statusDisplay: dto.status_display,
  blockReason: dto.block_reason,
  completionComment: dto.completion_comment,
  ...(dto.mandatory_tooltip != null && { mandatoryTooltip: dto.mandatory_tooltip }),
  ...(dto.reference != null && { reference: dto.reference }),
  ...(dto.relevant_for != null && { relevantFor: dto.relevant_for }),
  ...(dto.reported_to != null && { reportedTo: dto.reported_to }),
  ...(dto.tags != null && typeof dto.tags === 'object' && { tags: dto.tags }),
  ...(dto.assigned_to != null && { assignedTo: dto.assigned_to }),
  ...(dto.assigned_to_full_name != null && { assignedToFullName: dto.assigned_to_full_name }),
});

// --- Instance Comments (GET/POST asset-actions/{id}/comments/) ---
const parseIsTruncated = (value: boolean | string | undefined): boolean => {
  if (value === true || value === 'true' || value === '1') return true;
  return false;
};

export const parseInstanceCommentForFE = (dto: InstanceCommentDTO): InstanceComment => ({
  id: dto.id,
  text: dto.text,
  ...(dto.text_preview != null && { textPreview: dto.text_preview }),
  ...(dto.is_truncated !== undefined && { isTruncated: parseIsTruncated(dto.is_truncated) }),
  ...(dto.purpose !== undefined && { purpose: dto.purpose }),
  ...(dto.purpose_display != null && { purposeDisplay: dto.purpose_display }),
  userEmail: dto.user_email,
  userFullName: dto.user_full_name,
  createdAt: dto.created_at,
  updatedAt: dto.updated_at,
});

export const parseActionLibraryInstanceListForFE = (dto: ActionLibraryInstanceDTO): ActionLibraryInstance => ({
  id: dto.id,
  title: dto.title,
  description: dto.description,
  specialInstruction: dto.special_instruction,
  reportedTo: dto.reported_to,
  area: dto.area ?? undefined,
  areaDisplay: dto.area_display,
  areaData: dto.area_data ? parseInstanceAreaDataForFE(dto.area_data) : undefined,
  subArea: dto.sub_area ?? undefined,
  subAreaDisplay: dto.sub_area_display,
  subAreaData: dto.sub_area_data ? parseInstanceSubAreaDataForFE(dto.sub_area_data) : undefined,
  criticality: dto.criticality,
  deadline: dto.deadline,
  reminder: dto.reminder ?? null,
  status: dto.status,
  statusDisplay: dto.status_display,
  statusPercent: dto.status_percent,
  organization: dto.organization,
  asset: dto.asset,
  assetName: dto.asset_name,
  assetType: dto.asset_type,
  assetImportance: dto.asset_importance ?? undefined,
  assetImportanceDisplay: dto.asset_importance_display ?? undefined,
  owner: dto.owner,
  ownerFullName: dto.owner_full_name,
  delegatedTo: dto.delegated_to,
  delegatedToFullName: dto.delegated_to_full_name,
  delegationComment: dto.delegation_comment ?? undefined,
  templateId: dto.template ?? undefined,
  templateName: dto.template_name,
  templateReference: dto.template_reference ?? undefined,
  templateSummary: dto.template_summary,
  templateSource: dto.template_source,
  templateSourceDisplay: dto.template_source_display,
  templateTags: parseTemplateTagsFromDTO(dto.template_tags),
  validationPeriod: dto.validation_period ?? undefined,
  resultValidityPeriodMonths: dto.result_validity_period_months ?? null,
  validityPeriodMonths: dto.result_validity_period_months ?? dto.validity_period_months,
  validUntil: dto.valid_until ?? undefined,
  isCompleted: parseOptionalBooleanFromDTO(dto.is_completed),
  completedStepsCount: dto.completed_steps_count != null ? Number(dto.completed_steps_count) : undefined,
  totalStepsCount: dto.total_steps_count != null ? Number(dto.total_steps_count) : undefined,
  stepsCount: dto.steps_count != null ? Number(dto.steps_count) : undefined,
  canComplete: parseOptionalBooleanFromDTO(dto.can_complete),
  completeButtonTooltip: dto.complete_button_tooltip ?? undefined,
  canReopen: parseOptionalBooleanFromDTO(dto.can_reopen),
  canDelegate: parseOptionalBooleanFromDTO(dto.can_delegate) ?? false,
  canDelete: dto.can_delete === true,
  completionComment: dto.completion_comment ?? undefined,
  ...(dto.reference != null && { reference: dto.reference }),
  ...(dto.tags != null && typeof dto.tags === 'object' && { tags: dto.tags }),
  steps: dto.steps?.map(parseActionLibraryInstanceStepForFE),
  ...(dto.instance_comments_count != null && { instanceCommentsCount: dto.instance_comments_count }),
  ...(dto.last_instance_comment != null && {
    lastInstanceComment: parseInstanceCommentForFE(dto.last_instance_comment),
  }),
  ...(dto.last_instance_comment === null && { lastInstanceComment: null }),
  createdAt: dto.created_at,
  updatedAt: dto.updated_at,
});

export const parseAllActionLibraryInstancesForFE = (dtos: ActionLibraryInstanceDTO[]): ActionLibraryInstance[] =>
  dtos.map(parseActionLibraryInstanceListForFE);

// Bulk Create Parsers
const parseBulkCreateAdhocStepForBE = (step: BulkCreateActionLibraryInstanceAdhocStep): BulkCreateActionLibraryInstanceAdhocStepDTO => ({
  title: step.title,
  instruction: step.instruction,
  mandatory: step.mandatory,
  order: step.order,
});

const parseBulkCreateAdhocDataForBE = (data: BulkCreateActionLibraryInstanceAdhocData): BulkCreateActionLibraryInstanceAdhocDataDTO => ({
  title: data.title,
  description: data.description,
  special_instruction: data.specialInstruction,
  steps: data.steps.map(parseBulkCreateAdhocStepForBE),
});

const parseTemplateStepOverrideForBE = (override: TemplateStepOverride): TemplateStepOverrideDTO => {
  const dto: TemplateStepOverrideDTO = { step_id: override.stepId };
  if (override.mandatory !== undefined) dto.mandatory = override.mandatory;
  if (override.relevantFor !== undefined && override.relevantFor !== null) dto.relevant_for = override.relevantFor;
  if (override.reference !== undefined && override.reference !== null) dto.reference = override.reference;
  if (override.reportedTo !== undefined && override.reportedTo !== null) dto.reported_to = override.reportedTo;
  if (override.tags !== undefined && override.tags !== null) dto.tags = override.tags;
  return dto;
};

export const parseBulkCreateActionLibraryInstanceForBE = (
  payload: BulkCreateActionLibraryInstancePayload,
): BulkCreateActionLibraryInstanceDTO => ({
  asset_ids: payload.assetIds,
  template_id: payload.templateId || null,
  time_parameters: {
    deadline: payload.timeParameters.deadline,
    ...(payload.timeParameters.reminder != null &&
      payload.timeParameters.reminder !== '' && {
        reminder: payload.timeParameters.reminder,
      }),
  },
  adhoc_data: payload.adhocData ? parseBulkCreateAdhocDataForBE(payload.adhocData) : undefined,
  ...(payload.instanceName != null && payload.instanceName !== '' && { instance_name: payload.instanceName }),
  ...(payload.reportedTo != null && payload.reportedTo !== '' && { report_to: payload.reportedTo }),
  ...(payload.specialInstruction != null && payload.specialInstruction !== '' && { special_instruction: payload.specialInstruction }),
  ...(payload.templateStepOverrides != null &&
    payload.templateStepOverrides.length > 0 && {
      template_step_overrides: payload.templateStepOverrides.map(parseTemplateStepOverrideForBE),
    }),
  ...(payload.validityPeriodMonths != null && { validity_period_months: payload.validityPeriodMonths }),
  ...(payload.criticality && { criticality: payload.criticality }),
});

export const parseUpdateActionLibraryInstanceForBE = (payload: UpdateActionLibraryInstancePayload): Record<string, unknown> => {
  const base: Record<string, unknown> = {
    title: payload.title,
    deadline: payload.deadline,
    delegated_to: payload.delegatedTo,
    special_instruction: payload.specialInstruction,
    steps: payload.steps.map(step => ({
      id: step.id,
      mandatory: step.mandatory,
      ...(step.status != null && { status: step.status }),
    })),
    asset: payload.assetId,
  };
  if (payload.reminder != null && payload.reminder !== '') base.reminder = payload.reminder;
  if (payload.reportedTo !== undefined) base.reported_to = payload.reportedTo ?? '';
  if (payload.status != null) base.status = payload.status;
  if (payload.completionComment != null) base.completion_comment = payload.completionComment;
  if (payload.criticality != null) base.criticality = payload.criticality;
  if (payload.validityPeriodMonths !== undefined) base.validity_period_months = payload.validityPeriodMonths;
  return base;
};

export const parseDelegateActionLibraryInstanceForBE = (
  payload: DelegateActionLibraryInstancePayload,
): { delegated_to: number | null; delegation_comment: string } => ({
  delegated_to: payload.delegatedTo,
  delegation_comment: payload.delegationComment,
});

export const parseCompleteActionLibraryInstanceForBE = (
  payload: CompleteActionLibraryInstancePayload,
): { completion_comment?: string } => ({
  ...(payload.completionComment !== undefined &&
    payload.completionComment !== '' && {
      completion_comment: payload.completionComment,
    }),
});

/** POST asset-actions/{id}/reopen/ (AssetActionReopen). Backend schema: comment required. */
export const parseReopenInstanceForBE = (payload: ReopenInstancePayload): { comment: string } => ({
  comment: payload.comment,
});

/** POST asset-actions/{id}/return-to-owner/ (AssetActionReturnToOwner). Backend schema: delegation_comment required. */
export const parseReturnToOwnerActionLibraryInstanceForBE = (
  payload: ReturnToOwnerActionLibraryInstancePayload,
): { delegation_comment: string } => ({
  delegation_comment: payload.delegationComment,
});

// --- Instance Activity Log (GET asset-actions/{id}/status-logs/) ---
export const parseInstanceActivityLogEntryForFE = (dto: InstanceActivityLogEntryDTO): InstanceActivityLogEntry => ({
  id: dto.id,
  user: dto.user,
  userEmail: dto.user_email,
  userFullName: dto.user_full_name,
  ...(dto.message != null && { message: dto.message }),
  createdAt: dto.created_at,
});

export const parseInstanceActivityLogListForFE = (dtos: InstanceActivityLogEntryDTO[]): InstanceActivityLogEntry[] =>
  dtos.map(parseInstanceActivityLogEntryForFE);

export const parseInstanceCommentListForFE = (dtos: InstanceCommentDTO[]): InstanceComment[] => dtos.map(parseInstanceCommentForFE);

export const parseInstanceCommentCreateForBE = (payload: InstanceCommentCreatePayload): { text: string } => ({
  text: payload.text,
});

// Extract primitive ID from filter chip (avoids [object Object] when id/name is nested)
const toFilterId = (f: { id?: unknown; name?: unknown; value?: unknown; label?: unknown } | string | number): string | number | null => {
  if (f == null) return null;
  if (typeof f === 'string' || typeof f === 'number') return f;
  const raw = f.id ?? f.value ?? f.name ?? f.label;
  if (raw == null) return null;
  if (typeof raw === 'string' || typeof raw === 'number') return raw;
  return toFilterId(raw as { id?: unknown; name?: unknown }); // recurse once for nested
};

// Filter parser (area/sub_area aligned with Action Library templates parser)
export const parseFiltersForActionLibraryInstances = (filters: Record<string, any>) => {
  const params: Record<string, any> = {};

  if (filters.area?.length) {
    const areaId = toFilterId(filters.area[0]);
    if (areaId != null) params.area = typeof areaId === 'number' ? String(areaId) : areaId;
  }
  if (filters.subarea?.length) {
    const subAreaId = toFilterId(filters.subarea[0]);
    if (subAreaId != null) params.sub_area = typeof subAreaId === 'number' ? String(subAreaId) : subAreaId;
  }
  if (filters.asset_type?.length) {
    const [first] = filters.asset_type;
    const id = first?.id ?? first?.name ?? first;
    if (id != null) params.asset__type = Number(id);
  }
  if (filters.status?.length) {
    params.status = filters.status.map((f: any) => f.id ?? f.name ?? f).join(',');
  }
  if (filters.criticality?.length) {
    params.criticality = filters.criticality.map((f: any) => f.id ?? f.name ?? f).join(',');
  }
  if (filters.owner?.length) {
    params.owner = filters.owner.map((f: any) => f.id ?? f.name ?? f).join(',');
  }
  if (filters.due_date_up_to?.length) {
    // Handling date filtering. Assuming we take the latest date if multiple selected, or handle differently.
    // For simplicity, let's take the first one or assume single select.
    // If it's a range filter like "Next 7 days", we need to calculate the date.
    // But since we are just passing strings, let's assume the filter value IS the date string or close to it.
    // If it's "next_7_days", we need to compute it.
    // Let's defer computation to the caller or filter definition?
    // due_date_up_to: YYYY-MM-DD (API format)
    // But boolean dictionary uses 'true'/'false'.
    // If we use predefined ranges, we map them here.

    const filterVal = filters.due_date_up_to[0]?.id || filters.due_date_up_to[0];
    if (filterVal) {
      const today = new Date();
      if (filterVal === 'next_7_days') {
        const date = new Date(today);
        date.setDate(date.getDate() + 7);
        const [deadline] = date.toISOString().split('T');
        params.due_date_up_to = deadline;
      } else if (filterVal === 'next_30_days') {
        const date = new Date(today);
        date.setDate(date.getDate() + 30);
        const [deadline] = date.toISOString().split('T');
        params.due_date_up_to = deadline;
      } else if (filterVal === 'next_3_months') {
        const date = new Date(today);
        date.setMonth(date.getMonth() + 3);
        const [deadline] = date.toISOString().split('T');
        params.due_date_up_to = deadline;
      } else if (typeof filterVal === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(filterVal)) {
        params.due_date_up_to = filterVal;
      }
    }
  }
  if (filters.is_overdue?.length) {
    params.past_due_date = filters.is_overdue[0]?.id === 'true';
  }
  if (filters.past_reminder?.length) {
    params.past_reminder_date = filters.past_reminder[0]?.id === 'true';
  }
  if (filters.asset_importance?.length) {
    params.asset_importance = filters.asset_importance.map((f: any) => f.id ?? f.name ?? f).join(',');
  }
  if (filters.asset_id?.length) {
    params.asset_id = filters.asset_id.map((f: any) => f.id ?? f.name ?? f).join(',');
  }
  if (filters.responsible_user?.length) {
    params.responsible_user = filters.responsible_user.map((f: any) => f.id ?? f.name ?? f).join(',');
  }
  if (filters.instance_creation_date?.length) {
    const rangeId = filters.instance_creation_date[0]?.id ?? filters.instance_creation_date[0];
    if (rangeId) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let createdFrom: string | undefined;
      const [createdTo] = today.toISOString().split('T');
      if (rangeId === 'last_7_days') {
        const from = new Date(today);
        from.setDate(from.getDate() - 7);
        [createdFrom] = from.toISOString().split('T');
      } else if (rangeId === 'last_30_days') {
        const from = new Date(today);
        from.setDate(from.getDate() - 30);
        [createdFrom] = from.toISOString().split('T');
      } else if (rangeId === 'last_3_months') {
        const from = new Date(today);
        from.setMonth(from.getMonth() - 3);
        [createdFrom] = from.toISOString().split('T');
      } else if (rangeId === 'last_6_months') {
        const from = new Date(today);
        from.setMonth(from.getMonth() - 6);
        [createdFrom] = from.toISOString().split('T');
      } else if (rangeId === 'last_9_months') {
        const from = new Date(today);
        from.setMonth(from.getMonth() - 9);
        [createdFrom] = from.toISOString().split('T');
      } else if (rangeId === 'last_12_months') {
        const from = new Date(today);
        from.setMonth(from.getMonth() - 12);
        [createdFrom] = from.toISOString().split('T');
      }
      if (createdFrom) params.created_from = createdFrom;
      params.created_to = createdTo;
    }
  }
  if (filters.validity?.length) {
    const val = filters.validity[0]?.id ?? filters.validity[0];
    if (val === 'always_valid' || val === 'time_limited') params.validity = val;
  }
  if (filters.number_of_steps?.length) {
    const val = filters.number_of_steps[0]?.id ?? filters.number_of_steps[0];
    const num = Number(val);
    if (!Number.isNaN(num) && num >= 0) params.steps_count = num;
  }
  if (filters.tags?.length) {
    params.tags = filters.tags.map((f: any) => f.id ?? f.name ?? f).join(',');
  }
  // Source filter removed – instances are no longer creatable from blank templates

  return params;
};

/** Query string for GET /api/v1/asset-actions/export/ (UC19). Drops empty keys and pagination. */
export const parseActionLibraryInstanceExportParamsForBE = (params: ActionLibraryInstanceExportParams): ParamsType => {
  if (params.export_format !== 'csv' && params.export_format !== 'json') {
    throw new Error(`Invalid export_format: ${String(params.export_format)}`);
  }
  const result: ParamsType = {};
  (Object.keys(params) as (keyof ActionLibraryInstanceExportParams)[]).forEach(key => {
    const value = params[key];
    if (value === undefined || value === null) return;
    result[key as string] = value as ParamsType[string];
  });
  return result;
};

/** RFC 5987 `filename*` and legacy `filename=` from Content-Disposition. */
export const parseContentDispositionFilename = (header: string | undefined): string | null => {
  if (header == null || header === '') return null;
  const filenameStar = /filename\*=(?:UTF-8''|utf-8'')([^;\n]+)/i.exec(header);
  if (filenameStar?.[1]) {
    const raw = filenameStar[1].trim().replace(/^"(.*)"$/, '$1');
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted?.[1]) return quoted[1].trim();
  const unquoted = /filename=([^;\n]+)/i.exec(header);
  if (unquoted?.[1]) return unquoted[1].trim().replace(/^"(.*)"$/, '$1');
  return null;
};

export const resolveActionLibraryInstanceExportFilename = (
  contentDisposition: string | undefined,
  format: AssetActionInstanceExportFormat,
): string => parseContentDispositionFilename(contentDisposition) ?? ASSET_ACTION_INSTANCE_EXPORT_DEFAULT_FILENAMES[format];

export default parseFiltersForActionLibraryInstances;
